<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="cp-tabs">
            <button v-for="tab in tabs" :key="tab.key" type="button" class="cp-tab-btn"
                :class="{ 'cp-tab-btn--active': activeTab === tab.key }" @click="switchTab(tab.key)">
                {{ tab.label }}
            </button>
        </div>

        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <div class="ds-filters">
                    <div v-if="activeTab === 'areas'" class="ds-filter">
                        <label class="ds-filter__label">Inside/Outside</label>
                        <select v-model="s.filters.inside_outside" class="ds-select" @change="onFilterChange">
                            <option v-for="opt in insideOutsideFilterOptions" :key="'io-filter-' + opt.value"
                                :value="opt.value">
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>
                    <div class="ds-search">
                        <search :key="'search-' + activeTab" @on-search="onSearch" class-list="px-3 h-9"></search>
                    </div>
                </div>

                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>Export Excel</span>
                    </button>
                    <button v-if="canDelete" type="button" class="ds-btn ds-btn--danger"
                        :disabled="s.deletingSelected || s.selectedIds.length === 0" @click="confirmBulkDelete">
                        <font-awesome-icon icon="trash" class="mr-2" />
                        <span>{{ s.deletingSelected ? "Deleting..." : "Delete Selected" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--ghost" :disabled="s.bulkUploading"
                        @click="downloadBulkTemplate">
                        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                        <span>Download Template</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" :disabled="s.bulkUploading"
                        @click="openBulkUploadModal">
                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="mr-2" />
                        <span>{{ s.bulkUploading ? "Uploading..." : "Bulk Upload" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        {{ addButtonLabel }}
                    </button>
                </div>

                <input ref="bulkFileInput" type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style="display: none" @change="onBulkFileSelected" />
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div v-if="s.loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="!s.loading && s.items.length === 0"
                class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">🅿️</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ emptyTitle }}</h2>
                    <p class="text-gray-500 mb-4">{{ emptyHint }}</p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        {{ addButtonLabel }}
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr v-if="activeTab === 'areas'">
                                <th class="ds-th w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :checked="allVisibleSelected" @change="toggleSelectAll" />
                                </th>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Parking Location</th>
                                <th class="ds-th">Slots</th>
                                <th class="ds-th">VIP</th>
                                <th class="ds-th">Determination</th>
                                <th class="ds-th">Car Charge</th>
                                <th class="ds-th">Inside/Outside</th>
                                <th class="ds-th">Paid</th>
                                <th class="ds-th">Shaded</th>
                                <th class="ds-th">Underground</th>
                                <th class="ds-th">Sensor</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                            <tr v-else-if="activeTab === 'transport'">
                                <th class="ds-th w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :checked="allVisibleSelected" @change="toggleSelectAll" />
                                </th>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Service</th>
                                <th class="ds-th">Availability</th>
                                <th class="ds-th">No. Passengers</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                            <tr v-else>
                                <th class="ds-th w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :checked="allVisibleSelected" @change="toggleSelectAll" />
                                </th>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Service</th>
                                <th class="ds-th">Quantity</th>
                                <th class="ds-th">Location</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in s.items" :key="item.id" class="ds-row">
                                <td class="ds-td w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :value="String(item.id)" v-model="s.selectedIds" @click.stop />
                                </td>
                                <td class="ds-td text-gray-400 font-medium">{{ rowNumber(index) }}</td>

                                <template v-if="activeTab === 'areas'">
                                    <td class="ds-td font-medium">{{ item.parking_location || "—" }}</td>
                                    <td class="ds-td">{{ formatIntegerDisplay(item.parking_slots) }}</td>
                                    <td class="ds-td">{{ formatIntegerDisplay(item.vip_parking) }}</td>
                                    <td class="ds-td">{{ formatIntegerDisplay(item.determination) }}</td>
                                    <td class="ds-td">{{ formatBooleanDisplay(item.has_car_charge) }}</td>
                                    <td class="ds-td">{{ item.inside_outside || "—" }}</td>
                                    <td class="ds-td">{{ formatBooleanDisplay(item.is_paid_parking) }}</td>
                                    <td class="ds-td">{{ item.shaded || "—" }}</td>
                                    <td class="ds-td">{{ formatBooleanDisplay(item.is_underground) }}</td>
                                    <td class="ds-td">{{ formatBooleanDisplay(item.has_sensor) }}</td>
                                </template>

                                <template v-else-if="activeTab === 'transport'">
                                    <td class="ds-td font-medium">{{ item.service_name || "—" }}</td>
                                    <td class="ds-td">{{ item.availability || "—" }}</td>
                                    <td class="ds-td">{{ item.passenger_capacity || "—" }}</td>
                                </template>

                                <template v-else>
                                    <td class="ds-td font-medium">{{ item.service_name || "—" }}</td>
                                    <td class="ds-td">{{ formatIntegerDisplay(item.quantity) }}</td>
                                    <td class="ds-td">{{ item.location || "—" }}</td>
                                </template>

                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn"
                                        @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDelete" type="button" title="Delete"
                                        class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
        </div>

        <transition name="fade">
            <div v-if="s.showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm">
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">{{ formModalTitle }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ entity }} · {{ campus }}</p>
                    </div>

                    <div v-if="activeTab === 'areas'" class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div class="sm:col-span-2">
                            <label class="ds-label">Parking Location <span class="text-red-500">*</span></label>
                            <input v-model="s.form.parking_location" type="text" class="ds-input"
                                placeholder="Parking location" />
                        </div>
                        <div>
                            <label class="ds-label">Slots</label>
                            <input v-model="s.form.parking_slots" type="number" min="0" class="ds-input"
                                placeholder="Number of slots" />
                        </div>
                        <div>
                            <label class="ds-label">VIP</label>
                            <input v-model="s.form.vip_parking" type="number" min="0" class="ds-input"
                                placeholder="VIP parking slots" />
                        </div>
                        <div>
                            <label class="ds-label">Determination</label>
                            <input v-model="s.form.determination" type="number" min="0" class="ds-input"
                                placeholder="Determination" />
                        </div>
                        <div>
                            <label class="ds-label">Car Charge</label>
                            <select v-model="s.form.has_car_charge" class="ds-select">
                                <option v-for="opt in booleanFormOptions" :key="'cc-form-' + opt.value"
                                    :value="opt.value">{{ opt.label }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Inside/Outside</label>
                            <select v-model="s.form.inside_outside" class="ds-select">
                                <option value="">—</option>
                                <option value="Inside">Inside</option>
                                <option value="Outside">Outside</option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Paid</label>
                            <select v-model="s.form.is_paid_parking" class="ds-select">
                                <option v-for="opt in booleanFormOptions" :key="'paid-form-' + opt.value"
                                    :value="opt.value">{{ opt.label }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Shaded</label>
                            <input v-model="s.form.shaded" type="text" class="ds-input" placeholder="Shaded" />
                        </div>
                        <div>
                            <label class="ds-label">Underground</label>
                            <select v-model="s.form.is_underground" class="ds-select">
                                <option v-for="opt in booleanFormOptions" :key="'ug-form-' + opt.value"
                                    :value="opt.value">{{ opt.label }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Sensor</label>
                            <select v-model="s.form.has_sensor" class="ds-select">
                                <option v-for="opt in booleanFormOptions" :key="'sensor-form-' + opt.value"
                                    :value="opt.value">{{ opt.label }}</option>
                            </select>
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Remarks</label>
                            <textarea v-model="s.form.remarks" class="ds-input ds-textarea" rows="3"
                                placeholder="Optional remarks"></textarea>
                        </div>
                    </div>

                    <div v-else-if="activeTab === 'transport'" class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div class="sm:col-span-2">
                            <label class="ds-label">Service <span class="text-red-500">*</span></label>
                            <input v-model="s.form.service_name" type="text" class="ds-input"
                                placeholder="Service name" />
                        </div>
                        <div>
                            <label class="ds-label">Availability</label>
                            <input v-model="s.form.availability" type="text" class="ds-input"
                                placeholder="Availability" />
                        </div>
                        <div>
                            <label class="ds-label">No. Passengers</label>
                            <input v-model="s.form.passenger_capacity" type="text" class="ds-input"
                                placeholder="Passenger capacity" />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Remarks</label>
                            <textarea v-model="s.form.remarks" class="ds-input ds-textarea" rows="3"
                                placeholder="Optional remarks"></textarea>
                        </div>
                    </div>

                    <div v-else class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div class="sm:col-span-2">
                            <label class="ds-label">Service <span class="text-red-500">*</span></label>
                            <input v-model="s.form.service_name" type="text" class="ds-input"
                                placeholder="Service name" />
                        </div>
                        <div>
                            <label class="ds-label">Quantity</label>
                            <input v-model="s.form.quantity" type="number" min="0" class="ds-input"
                                placeholder="Quantity" />
                        </div>
                        <div>
                            <label class="ds-label">Location</label>
                            <input v-model="s.form.location" type="text" class="ds-input" placeholder="Location" />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Remarks</label>
                            <textarea v-model="s.form.remarks" class="ds-input ds-textarea" rows="3"
                                placeholder="Optional remarks"></textarea>
                        </div>
                    </div>

                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">Cancel</button>
                        <base-button type="button" :disabled="s.saving" class="bg-teal-700 hover:bg-teal-800 text-white"
                            @click="saveForm">
                            {{ s.saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <base-modal v-if="s.showBulkUploadModal" :title="bulkModalTitle" @close="closeBulkUploadModal">
                <div class="ds-bulk">
                    <div class="ds-bulk__banner">
                        <div class="ds-bulk__banner-icon">
                            <font-awesome-icon :icon="['fas', 'file-excel']" />
                        </div>
                        <div class="ds-bulk__banner-body">
                            <p class="ds-bulk__banner-title">{{ bulkModalTitle }}</p>
                            <p class="ds-bulk__banner-hint">
                                Entity and campus are automatic. Use the template and keep column headers unchanged.
                            </p>
                        </div>
                    </div>

                    <div v-if="s.bulkStep === 'select'" class="ds-bulk__section">
                        <div class="ds-bulk__dropzone" :class="{ 'ds-bulk__dropzone--active': s.bulkDragOver }"
                            @click="triggerBulkFilePicker" @dragover.prevent="s.bulkDragOver = true"
                            @dragleave.prevent="s.bulkDragOver = false" @drop.prevent="onBulkFileDrop">
                            <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="ds-bulk__dropzone-icon" />
                            <p class="ds-bulk__dropzone-title">Drop your Excel file here, or click to browse</p>
                            <p class="ds-bulk__dropzone-sub">Accepted formats: .xlsx, .xls</p>
                        </div>
                        <div class="ds-bulk__tips">
                            <button type="button" class="ds-bulk__link-btn" :disabled="s.bulkUploading"
                                @click.stop="downloadBulkTemplate">
                                <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                                Download Excel template
                            </button>
                            <p class="ds-bulk__tips-text">{{ bulkTemplateHint }}</p>
                        </div>
                    </div>

                    <div v-else-if="s.bulkStep === 'review'" class="ds-bulk__section">
                        <div class="ds-bulk__file-card">
                            <div class="ds-bulk__file-icon">
                                <font-awesome-icon :icon="['fas', 'file-excel']" />
                            </div>
                            <div class="min-w-0 flex-1">
                                <p class="ds-bulk__file-name">{{ s.bulkFileName }}</p>
                                <p class="ds-bulk__file-meta">
                                    {{ s.bulkValidRows.length }} valid row{{ s.bulkValidRows.length === 1 ? "" : "s" }}
                                    <span v-if="s.bulkValidationErrors.length">
                                        · {{ s.bulkValidationErrors.length }} issue{{ s.bulkValidationErrors.length === 1 ? "" : "s" }}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div v-if="s.bulkValidationErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                Fix the following before upload
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div v-for="(err, idx) in s.bulkValidationErrors" :key="'bulk-err-' + idx"
                                    class="ds-bulk__error-item">
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="ds-bulk__ready">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="ds-bulk__ready-icon" />
                            <div>
                                <p class="ds-bulk__ready-title">Ready to upload {{ s.bulkValidRows.length }} record{{ s.bulkValidRows.length === 1 ? "" : "s" }}</p>
                                <p class="ds-bulk__ready-sub">We will create rows for every valid entry.</p>
                            </div>
                        </div>
                        <div class="ds-bulk__footer">
                            <button type="button" class="ds-btn ds-btn--ghost" :disabled="s.bulkUploading"
                                @click="resetBulkSelection">Change file</button>
                            <button type="button" class="ds-btn ds-btn--primary"
                                :disabled="s.bulkUploading || !s.bulkValidRows.length || s.bulkValidationErrors.length"
                                @click="startBulkUpload">Start Upload</button>
                        </div>
                    </div>

                    <div v-else-if="s.bulkStep === 'uploading'" class="ds-bulk__section">
                        <div class="ds-bulk__progress-card">
                            <div class="ds-bulk__progress-head">
                                <p class="ds-bulk__progress-title">Uploading...</p>
                                <p class="ds-bulk__progress-percent">{{ bulkProgressPercent }}%</p>
                            </div>
                            <div class="ds-bulk__progress-track">
                                <div class="ds-bulk__progress-fill" :style="{ width: bulkProgressPercent + '%' }"></div>
                            </div>
                            <p class="ds-bulk__progress-sub">
                                {{ s.bulkProgressCurrent }} / {{ s.bulkProgressTotal }} · {{ bulkProgressPercent }}%
                            </p>
                            <p v-if="s.bulkCurrentName" class="ds-bulk__progress-name">{{ s.bulkCurrentName }}</p>
                        </div>
                    </div>

                    <div v-else-if="s.bulkStep === 'done'" class="ds-bulk__section">
                        <div class="ds-bulk__summary">
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--ok">
                                <p class="ds-bulk__summary-value">{{ s.bulkSuccessCount }}</p>
                                <p class="ds-bulk__summary-label">Successful</p>
                            </div>
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--fail">
                                <p class="ds-bulk__summary-value">{{ s.bulkFailCount }}</p>
                                <p class="ds-bulk__summary-label">Failed</p>
                            </div>
                            <div class="ds-bulk__summary-stat">
                                <p class="ds-bulk__summary-value">{{ s.bulkProgressTotal }}</p>
                                <p class="ds-bulk__summary-label">Total</p>
                            </div>
                        </div>
                        <div v-if="s.bulkUploadErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-2" />
                                Upload errors
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div v-for="(err, idx) in s.bulkUploadErrors" :key="'bulk-up-err-' + idx"
                                    class="ds-bulk__error-item">
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="ds-bulk__footer ds-bulk__footer--single">
                            <button type="button" class="ds-btn ds-btn--primary" @click="closeBulkUploadModal">Done</button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import Search from "../../../../components/shared/search.vue";

const AREAS_LIST_QUERY = gql`
    query FacilityParkingAreas(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $inside_outside: String
        $search: String
    ) {
        facilityParkingAreas(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            inside_outside: $inside_outside
            search: $search
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                parking_location
                parking_slots
                vip_parking
                determination
                has_car_charge
                inside_outside
                is_paid_parking
                shaded
                is_underground
                has_sensor
                remarks
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const TRANSPORT_LIST_QUERY = gql`
    query FacilityParkingTransportServices(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $search: String
    ) {
        facilityParkingTransportServices(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            search: $search
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                service_name
                availability
                passenger_capacity
                remarks
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const HELIPAD_LIST_QUERY = gql`
    query FacilityParkingHelipads(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $search: String
    ) {
        facilityParkingHelipads(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            search: $search
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                service_name
                quantity
                location
                remarks
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const CREATE_AREA_MUTATION = gql`
    mutation CreateFacilityParkingArea($input: FacilityParkingAreaInput!) {
        createFacilityParkingArea(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_AREA_MUTATION = gql`
    mutation UpdateFacilityParkingArea($id: ID!, $input: FacilityParkingAreaInput!) {
        updateFacilityParkingArea(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_AREA_MUTATION = gql`
    mutation DeleteFacilityParkingArea($id: ID!) {
        deleteFacilityParkingArea(id: $id) {
            error
            error_code
            message
        }
    }
`;

const CREATE_AREAS_BULK_MUTATION = gql`
    mutation CreateFacilityParkingAreasBulk($input: CreateFacilityParkingAreasBulkInput!) {
        createFacilityParkingAreasBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    row
                    parking_location
                    error
                    message
                }
            }
        }
    }
`;

const DELETE_AREAS_BULK_MUTATION = gql`
    mutation DeleteFacilityParkingAreasBulk($input: DeleteFacilityParkingAreasBulkInput!) {
        deleteFacilityParkingAreasBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    id
                    error
                    message
                }
            }
        }
    }
`;

const CREATE_TRANSPORT_MUTATION = gql`
    mutation CreateFacilityParkingTransportService($input: FacilityParkingTransportServiceInput!) {
        createFacilityParkingTransportService(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_TRANSPORT_MUTATION = gql`
    mutation UpdateFacilityParkingTransportService($id: ID!, $input: FacilityParkingTransportServiceInput!) {
        updateFacilityParkingTransportService(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_TRANSPORT_MUTATION = gql`
    mutation DeleteFacilityParkingTransportService($id: ID!) {
        deleteFacilityParkingTransportService(id: $id) {
            error
            error_code
            message
        }
    }
`;

const CREATE_TRANSPORTS_BULK_MUTATION = gql`
    mutation CreateFacilityParkingTransportServicesBulk($input: CreateFacilityParkingTransportServicesBulkInput!) {
        createFacilityParkingTransportServicesBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    row
                    service_name
                    error
                    message
                }
            }
        }
    }
`;

const DELETE_TRANSPORTS_BULK_MUTATION = gql`
    mutation DeleteFacilityParkingTransportServicesBulk($input: DeleteFacilityParkingTransportServicesBulkInput!) {
        deleteFacilityParkingTransportServicesBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    id
                    error
                    message
                }
            }
        }
    }
`;

const CREATE_HELIPAD_MUTATION = gql`
    mutation CreateFacilityParkingHelipad($input: FacilityParkingHelipadInput!) {
        createFacilityParkingHelipad(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_HELIPAD_MUTATION = gql`
    mutation UpdateFacilityParkingHelipad($id: ID!, $input: FacilityParkingHelipadInput!) {
        updateFacilityParkingHelipad(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_HELIPAD_MUTATION = gql`
    mutation DeleteFacilityParkingHelipad($id: ID!) {
        deleteFacilityParkingHelipad(id: $id) {
            error
            error_code
            message
        }
    }
`;

const CREATE_HELIPADS_BULK_MUTATION = gql`
    mutation CreateFacilityParkingHelipadsBulk($input: CreateFacilityParkingHelipadsBulkInput!) {
        createFacilityParkingHelipadsBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    row
                    service_name
                    error
                    message
                }
            }
        }
    }
`;

const DELETE_HELIPADS_BULK_MUTATION = gql`
    mutation DeleteFacilityParkingHelipadsBulk($input: DeleteFacilityParkingHelipadsBulkInput!) {
        deleteFacilityParkingHelipadsBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    id
                    error
                    message
                }
            }
        }
    }
`;

const BULK_TEMPLATES = {
    areas: {
        filename: "adpoly-facility-parking-areas-bulk-upload-template.xlsx",
        url: require("../../../../assets/templates/adpoly-facility-parking-areas-bulk-upload-template.xlsx"),
    },
    transport: {
        filename: "adpoly-facility-parking-transport-services-bulk-upload-template.xlsx",
        url: require("../../../../assets/templates/adpoly-facility-parking-transport-services-bulk-upload-template.xlsx"),
    },
    helipad: {
        filename: "adpoly-facility-parking-helipads-bulk-upload-template.xlsx",
        url: require("../../../../assets/templates/adpoly-facility-parking-helipads-bulk-upload-template.xlsx"),
    },
};

const QUERY_CONFIG = {
    areas: {
        listQuery: AREAS_LIST_QUERY,
        listRoot: "facilityParkingAreas",
        createMutation: CREATE_AREA_MUTATION,
        createRoot: "createFacilityParkingArea",
        updateMutation: UPDATE_AREA_MUTATION,
        updateRoot: "updateFacilityParkingArea",
        deleteMutation: DELETE_AREA_MUTATION,
        deleteRoot: "deleteFacilityParkingArea",
        createBulkMutation: CREATE_AREAS_BULK_MUTATION,
        createBulkRoot: "createFacilityParkingAreasBulk",
        deleteBulkMutation: DELETE_AREAS_BULK_MUTATION,
        deleteBulkRoot: "deleteFacilityParkingAreasBulk",
        exportSuffix: "Parking_Areas",
        loadError: "Failed to load parking areas",
        deleteTitle: "Delete this parking area?",
        bulkDeleteTitle: "Delete selected parking areas?",
        bulkLabelField: "parking_location",
    },
    transport: {
        listQuery: TRANSPORT_LIST_QUERY,
        listRoot: "facilityParkingTransportServices",
        createMutation: CREATE_TRANSPORT_MUTATION,
        createRoot: "createFacilityParkingTransportService",
        updateMutation: UPDATE_TRANSPORT_MUTATION,
        updateRoot: "updateFacilityParkingTransportService",
        deleteMutation: DELETE_TRANSPORT_MUTATION,
        deleteRoot: "deleteFacilityParkingTransportService",
        createBulkMutation: CREATE_TRANSPORTS_BULK_MUTATION,
        createBulkRoot: "createFacilityParkingTransportServicesBulk",
        deleteBulkMutation: DELETE_TRANSPORTS_BULK_MUTATION,
        deleteBulkRoot: "deleteFacilityParkingTransportServicesBulk",
        exportSuffix: "Parking_Transport_Services",
        loadError: "Failed to load transport services",
        deleteTitle: "Delete this transport service?",
        bulkDeleteTitle: "Delete selected transport services?",
        bulkLabelField: "service_name",
    },
    helipad: {
        listQuery: HELIPAD_LIST_QUERY,
        listRoot: "facilityParkingHelipads",
        createMutation: CREATE_HELIPAD_MUTATION,
        createRoot: "createFacilityParkingHelipad",
        updateMutation: UPDATE_HELIPAD_MUTATION,
        updateRoot: "updateFacilityParkingHelipad",
        deleteMutation: DELETE_HELIPAD_MUTATION,
        deleteRoot: "deleteFacilityParkingHelipad",
        createBulkMutation: CREATE_HELIPADS_BULK_MUTATION,
        createBulkRoot: "createFacilityParkingHelipadsBulk",
        deleteBulkMutation: DELETE_HELIPADS_BULK_MUTATION,
        deleteBulkRoot: "deleteFacilityParkingHelipadsBulk",
        exportSuffix: "Parking_Helipads",
        loadError: "Failed to load helipads",
        deleteTitle: "Delete this helipad?",
        bulkDeleteTitle: "Delete selected helipads?",
        bulkLabelField: "service_name",
    },
};

function emptySectionState(sectionKey) {
    var filters = { inside_outside: "" };
    if (sectionKey !== "areas") {
        filters = {};
    }

    var form = {};
    if (sectionKey === "areas") {
        form = {
            parking_location: "",
            parking_slots: "",
            vip_parking: "",
            determination: "",
            has_car_charge: "",
            inside_outside: "",
            is_paid_parking: "",
            shaded: "",
            is_underground: "",
            has_sensor: "",
            remarks: "",
        };
    } else if (sectionKey === "transport") {
        form = {
            service_name: "",
            availability: "",
            passenger_capacity: "",
            remarks: "",
        };
    } else {
        form = {
            service_name: "",
            quantity: "",
            location: "",
            remarks: "",
        };
    }

    return {
        items: [],
        paginatorInfo: null,
        loading: false,
        isExportExcel: false,
        selectedIds: [],
        deletingSelected: false,
        generatedReport: [],
        showBulkUploadModal: false,
        bulkStep: "select",
        bulkDragOver: false,
        bulkFileName: "",
        bulkValidRows: [],
        bulkValidationErrors: [],
        bulkUploading: false,
        bulkProgressCurrent: 0,
        bulkProgressTotal: 0,
        bulkCurrentName: "",
        bulkSuccessCount: 0,
        bulkFailCount: 0,
        bulkUploadErrors: [],
        page: 1,
        search: "",
        filters: filters,
        showFormModal: false,
        editingId: null,
        saving: false,
        form: form,
    };
}

function normalizeHeader(value) {
    if (value === null || value === undefined) return "";
    return String(value).replace(/\u00a0/g, " ").trim().replace(/\s+/g, " ").toUpperCase();
}

function cellToString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
        if (Math.floor(value) === value) return String(Math.trunc(value));
        return String(value);
    }
    return String(value).replace(/\u00a0/g, " ").trim();
}

function normalizeDashAsEmpty(value) {
    var v = cellToString(value);
    if (v === "-" || v.toLowerCase() === "none" || v.toLowerCase() === "n/a") return "";
    return v;
}

function resolveColumnIndex(headerIndex, canonical, alternates) {
    if (headerIndex[canonical] !== undefined) return headerIndex[canonical];
    if (alternates && alternates.length) {
        for (var a = 0; a < alternates.length; a++) {
            if (headerIndex[alternates[a]] !== undefined) return headerIndex[alternates[a]];
        }
    }
    return undefined;
}

function cellAt(row, index) {
    if (index === undefined) return "";
    return normalizeDashAsEmpty(row[index]);
}

function parseIntegerValue(value, label) {
    if (value === null || value === undefined) return { value: null, error: null };
    if (typeof value === "number") {
        if (value < 0 || Math.floor(value) !== value) {
            return { value: null, error: label + " must be a whole number greater than or equal to 0." };
        }
        return { value: Math.trunc(value), error: null };
    }
    var v = cellToString(value);
    if (v === "") return { value: null, error: null };
    if (!/^\d+$/.test(v)) {
        return { value: null, error: label + " must be a whole number greater than or equal to 0." };
    }
    return { value: parseInt(v, 10), error: null };
}

function parseBooleanValue(value) {
    if (value === null || value === undefined) return { value: null, error: null };
    if (typeof value === "boolean") return { value: value, error: null };
    if (typeof value === "number") {
        if (value === 1) return { value: true, error: null };
        if (value === 0) return { value: false, error: null };
        return { value: null, error: "Invalid yes/no value." };
    }
    var v = cellToString(value);
    if (v === "") return { value: null, error: null };
    var lower = v.toLowerCase();
    if (lower === "yes" || lower === "y" || lower === "true" || lower === "1") return { value: true, error: null };
    if (lower === "no" || lower === "n" || lower === "false" || lower === "0") return { value: false, error: null };
    return { value: null, error: "Invalid yes/no value. Use Yes or No." };
}

function formBooleanToValue(value) {
    if (value === "yes") return true;
    if (value === "no") return false;
    return null;
}

function booleanToFormValue(value) {
    if (value === true) return "yes";
    if (value === false) return "no";
    return "";
}

export default {
    name: "CarParkingModule",
    components: { Pagination, TableShimmer, Search },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
    },
    emits: ["changed"],
    data() {
        return {
            activeTab: "areas",
            perPage: 25,
            tabs: [
                { key: "areas", label: "Parking Areas" },
                { key: "transport", label: "Transport Services" },
                { key: "helipad", label: "Helipad" },
            ],
            insideOutsideFilterOptions: [
                { value: "", label: "All" },
                { value: "Inside", label: "Inside" },
                { value: "Outside", label: "Outside" },
            ],
            booleanFormOptions: [
                { value: "", label: "—" },
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
            ],
            tabState: {
                areas: emptySectionState("areas"),
                transport: emptySectionState("transport"),
                helipad: emptySectionState("helipad"),
            },
        };
    },
    computed: {
        s() {
            return this.tabState[this.activeTab];
        },
        cfg() {
            return QUERY_CONFIG[this.activeTab];
        },
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("facility-parking-create");
        },
        canUpdate() {
            return this.hasPermission("facility-parking-update");
        },
        canDelete() {
            return this.hasPermission("facility-parking-delete");
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        paginatorInfoWithItems() {
            if (!this.s.paginatorInfo) return null;
            var pi = this.s.paginatorInfo;
            var firstItem = pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1;
            var lastItem = pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total);
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: firstItem,
                lastItem: lastItem,
            };
        },
        bulkProgressPercent() {
            if (!this.s.bulkProgressTotal) return 0;
            return Math.round((this.s.bulkProgressCurrent / this.s.bulkProgressTotal) * 100);
        },
        allVisibleSelected() {
            if (!this.s.items || this.s.items.length === 0) return false;
            for (var i = 0; i < this.s.items.length; i++) {
                if (this.s.selectedIds.indexOf(String(this.s.items[i].id)) === -1) return false;
            }
            return true;
        },
        tableHeaders() {
            if (this.activeTab === "areas") {
                return ["Select", "#", "Parking Location", "Slots", "VIP", "Determination", "Car Charge", "Inside/Outside", "Paid", "Shaded", "Underground", "Sensor", "Actions"];
            }
            if (this.activeTab === "transport") {
                return ["Select", "#", "Service", "Availability", "No. Passengers", "Actions"];
            }
            return ["Select", "#", "Service", "Quantity", "Location", "Actions"];
        },
        addButtonLabel() {
            if (this.activeTab === "areas") return "Add parking area";
            if (this.activeTab === "transport") return "Add transport service";
            return "Add helipad";
        },
        emptyTitle() {
            if (this.activeTab === "areas") return "No parking areas found";
            if (this.activeTab === "transport") return "No transport services found";
            return "No helipads found";
        },
        emptyHint() {
            return "Try adjusting filters, or add a new record.";
        },
        formModalTitle() {
            var action = this.s.editingId ? "Edit" : "Add";
            if (this.activeTab === "areas") return action + " parking area";
            if (this.activeTab === "transport") return action + " transport service";
            return action + " helipad";
        },
        bulkModalTitle() {
            if (this.activeTab === "areas") return "Bulk Upload Parking Areas";
            if (this.activeTab === "transport") return "Bulk Upload Transport Services";
            return "Bulk Upload Helipads";
        },
        bulkTemplateHint() {
            if (this.activeTab === "areas") {
                return "Required column: Parking Location. Optional: Slots, VIP, Determination, Car Charge, Inside/Outside, Paid, Shaded, Underground, Sensor, Remarks.";
            }
            if (this.activeTab === "transport") {
                return "Required column: Service. Optional: Availability, No. Passengers, Remarks.";
            }
            return "Required column: Service. Optional: Quantity, Location, Remarks.";
        },
    },
    created() {
        this.loadList();
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        switchTab(tabKey) {
            if (this.activeTab === tabKey) return;
            this.activeTab = tabKey;
            this.loadList();
        },
        formatBooleanDisplay(value) {
            if (value === true) return "Yes";
            if (value === false) return "No";
            return "—";
        },
        formatIntegerDisplay(value) {
            if (value === null || value === undefined || value === "") return "—";
            return String(value);
        },
        listQueryVariables(page, first) {
            var vars = {
                first: first,
                page: page,
                entity: this.entity,
                campus: this.campus,
                search: this.s.search || null,
            };
            if (this.activeTab === "areas") {
                vars.inside_outside = this.s.filters.inside_outside || null;
            }
            return vars;
        },
        onFilterChange() {
            this.s.page = 1;
            this.loadList();
        },
        rowNumber(index) {
            if (!this.s.paginatorInfo) return index + 1;
            return (this.s.paginatorInfo.firstItem || 1) + index;
        },
        toggleSelectAll() {
            if (!this.canDelete) return;
            var ids = this.s.items.map(function (item) { return String(item.id); });
            if (!ids.length) return;
            if (this.allVisibleSelected) {
                this.s.selectedIds = this.s.selectedIds.filter(function (id) {
                    return ids.indexOf(id) === -1;
                });
            } else {
                var merged = this.s.selectedIds.concat(ids);
                var unique = [];
                for (var i = 0; i < merged.length; i++) {
                    if (unique.indexOf(merged[i]) === -1) unique.push(merged[i]);
                }
                this.s.selectedIds = unique;
            }
        },
        confirmBulkDelete() {
            var self = this;
            if (!this.$swal || !this.s.selectedIds.length) return;
            this.$swal.fire({
                title: this.cfg.bulkDeleteTitle,
                html: "<div class='text-left text-sm text-gray-600'><div><strong>Campus:</strong> " + this.campus + "</div><div><strong>Records:</strong> " + this.s.selectedIds.length + "</div></div>",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Yes, delete",
            }).then(function (result) {
                if (result && result.isConfirmed) self.deleteSelected();
            });
        },
        onSearch(value) {
            this.s.search = value || "";
            this.s.page = 1;
            this.loadList();
        },
        setPage(page) {
            this.s.page = page;
            this.loadList();
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if ((merged.icon === "error" || merged.icon === "warning") && (options.timer === undefined || options.timer === null)) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadList() {
            var self = this;
            var section = this.s;
            var cfg = this.cfg;
            section.loading = true;
            section.selectedIds = [];
            this.$apollo.query({
                query: cfg.listQuery,
                variables: this.listQueryVariables(section.page, this.perPage),
                fetchPolicy: "network-only",
            }).then(function (result) {
                var data = result && result.data ? result.data : null;
                var payload = data && data[cfg.listRoot] ? data[cfg.listRoot] : null;
                if (payload && !payload.error) {
                    section.items = payload.data || [];
                    section.paginatorInfo = payload.paginatorInfo || null;
                } else {
                    section.items = [];
                    section.paginatorInfo = null;
                    section.selectedIds = [];
                    self.toastAlert({ icon: "error", title: (payload && payload.message) || cfg.loadError });
                }
            }).catch(function (err) {
                section.items = [];
                section.paginatorInfo = null;
                section.selectedIds = [];
                self.toastAlert({ icon: "error", title: (err && err.message) || cfg.loadError });
            }).finally(function () {
                section.loading = false;
            });
        },
        openCreate() {
            this.s.editingId = null;
            this.s.form = Object.assign({}, emptySectionState(this.activeTab).form);
            this.s.showFormModal = true;
        },
        openEdit(item) {
            this.s.editingId = item.id;
            if (this.activeTab === "areas") {
                this.s.form = {
                    parking_location: item.parking_location || "",
                    parking_slots: item.parking_slots !== null && item.parking_slots !== undefined ? String(item.parking_slots) : "",
                    vip_parking: item.vip_parking !== null && item.vip_parking !== undefined ? String(item.vip_parking) : "",
                    determination: item.determination !== null && item.determination !== undefined ? String(item.determination) : "",
                    has_car_charge: booleanToFormValue(item.has_car_charge),
                    inside_outside: item.inside_outside || "",
                    is_paid_parking: booleanToFormValue(item.is_paid_parking),
                    shaded: item.shaded || "",
                    is_underground: booleanToFormValue(item.is_underground),
                    has_sensor: booleanToFormValue(item.has_sensor),
                    remarks: item.remarks || "",
                };
            } else if (this.activeTab === "transport") {
                this.s.form = {
                    service_name: item.service_name || "",
                    availability: item.availability || "",
                    passenger_capacity: item.passenger_capacity || "",
                    remarks: item.remarks || "",
                };
            } else {
                this.s.form = {
                    service_name: item.service_name || "",
                    quantity: item.quantity !== null && item.quantity !== undefined ? String(item.quantity) : "",
                    location: item.location || "",
                    remarks: item.remarks || "",
                };
            }
            this.s.showFormModal = true;
        },
        closeForm() {
            this.s.showFormModal = false;
            this.s.editingId = null;
            this.s.form = Object.assign({}, emptySectionState(this.activeTab).form);
        },
        buildInput() {
            var input = { entity: this.entity, campus: this.campus };
            if (this.activeTab === "areas") {
                var slots = parseIntegerValue(this.s.form.parking_slots, "Slots");
                var vip = parseIntegerValue(this.s.form.vip_parking, "VIP");
                var det = parseIntegerValue(this.s.form.determination, "Determination");
                input.parking_location = this.s.form.parking_location || null;
                input.parking_slots = slots.value;
                input.vip_parking = vip.value;
                input.determination = det.value;
                input.has_car_charge = formBooleanToValue(this.s.form.has_car_charge);
                input.inside_outside = this.s.form.inside_outside || null;
                input.is_paid_parking = formBooleanToValue(this.s.form.is_paid_parking);
                input.shaded = this.s.form.shaded || null;
                input.is_underground = formBooleanToValue(this.s.form.is_underground);
                input.has_sensor = formBooleanToValue(this.s.form.has_sensor);
                input.remarks = this.s.form.remarks || null;
            } else if (this.activeTab === "transport") {
                input.service_name = this.s.form.service_name || null;
                input.availability = this.s.form.availability || null;
                input.passenger_capacity = this.s.form.passenger_capacity || null;
                input.remarks = this.s.form.remarks || null;
            } else {
                var qty = parseIntegerValue(this.s.form.quantity, "Quantity");
                input.service_name = this.s.form.service_name || null;
                input.quantity = qty.value;
                input.location = this.s.form.location || null;
                input.remarks = this.s.form.remarks || null;
            }
            return input;
        },
        validateFormInput() {
            if (this.activeTab === "areas") {
                if (!this.s.form.parking_location || !String(this.s.form.parking_location).trim()) {
                    return "Parking location is required";
                }
                var checks = [
                    parseIntegerValue(this.s.form.parking_slots, "Slots"),
                    parseIntegerValue(this.s.form.vip_parking, "VIP"),
                    parseIntegerValue(this.s.form.determination, "Determination"),
                ];
                for (var i = 0; i < checks.length; i++) {
                    if (checks[i].error) return checks[i].error;
                }
            } else if (this.activeTab === "transport") {
                if (!this.s.form.service_name || !String(this.s.form.service_name).trim()) {
                    return "Service name is required";
                }
            } else {
                if (!this.s.form.service_name || !String(this.s.form.service_name).trim()) {
                    return "Service name is required";
                }
                var q = parseIntegerValue(this.s.form.quantity, "Quantity");
                if (q.error) return q.error;
            }
            return null;
        },
        saveForm() {
            var self = this;
            var validationError = this.validateFormInput();
            if (validationError) {
                this.toastAlert({ icon: "warning", title: validationError });
                return;
            }
            this.s.saving = true;
            var input = this.buildInput();
            var cfg = this.cfg;
            var request = this.s.editingId
                ? this.$apollo.mutate({ mutation: cfg.updateMutation, variables: { id: this.s.editingId, input: input } })
                : this.$apollo.mutate({ mutation: cfg.createMutation, variables: { input: input } });
            request.then(function (res) {
                var data = res && res.data ? res.data : null;
                var payload = self.s.editingId
                    ? (data && data[cfg.updateRoot] ? data[cfg.updateRoot] : null)
                    : (data && data[cfg.createRoot] ? data[cfg.createRoot] : null);
                if (payload && !payload.error) {
                    self.toastAlert({ icon: "success", title: payload.message || "Saved successfully" });
                    self.closeForm();
                    self.loadList();
                    self.$emit("changed");
                } else {
                    self.toastAlert({ icon: "error", title: (payload && payload.message) || "Save failed" });
                }
            }).catch(function (err) {
                self.toastAlert({ icon: "error", title: (err && err.message) || "Save failed" });
            }).finally(function () {
                self.s.saving = false;
            });
        },
        confirmDelete(item) {
            var self = this;
            if (!this.$swal) return;
            var label = this.activeTab === "areas"
                ? (item.parking_location || "—")
                : (item.service_name || "—");
            this.$swal.fire({
                title: this.cfg.deleteTitle,
                html: "<div class='text-left text-sm text-gray-600'><div><strong>Record:</strong> " + label + "</div></div>",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Yes, delete",
            }).then(function (result) {
                if (result && result.isConfirmed) self.deleteItem(item);
            });
        },
        deleteItem(item) {
            var self = this;
            var cfg = this.cfg;
            this.$apollo.mutate({ mutation: cfg.deleteMutation, variables: { id: item.id } }).then(function (res) {
                var data = res && res.data ? res.data : null;
                var payload = data && data[cfg.deleteRoot] ? data[cfg.deleteRoot] : null;
                if (payload && !payload.error) {
                    self.toastAlert({ icon: "success", title: payload.message || "Deleted" });
                    if (self.s.items.length === 1 && self.s.page > 1) self.s.page = self.s.page - 1;
                    self.loadList();
                    self.$emit("changed");
                } else {
                    self.toastAlert({ icon: "error", title: (payload && payload.message) || "Delete failed" });
                }
            }).catch(function (err) {
                self.toastAlert({ icon: "error", title: (err && err.message) || "Delete failed" });
            });
        },
        deleteSelected() {
            var self = this;
            var section = this.s;
            var cfg = this.cfg;
            if (!section.selectedIds.length || section.deletingSelected) return;
            section.deletingSelected = true;
            if (section.items && section.items.length === section.selectedIds.length && section.page > 1) {
                section.page = section.page - 1;
            }
            this.$apollo.mutate({
                mutation: cfg.deleteBulkMutation,
                variables: { input: { entity: this.entity, campus: this.campus, ids: section.selectedIds } },
            }).then(function (res) {
                var data = res && res.data ? res.data : null;
                var payload = data && data[cfg.deleteBulkRoot] ? data[cfg.deleteBulkRoot] : null;
                if (payload && !payload.error) {
                    self.toastAlert({ icon: "success", title: payload.message || "Selected records deleted successfully" });
                } else {
                    self.toastAlert({ icon: "error", title: (payload && payload.message) || "Bulk delete failed" });
                }
                section.selectedIds = [];
                self.loadList();
                self.$emit("changed");
            }).catch(function (err) {
                self.toastAlert({ icon: "error", title: (err && err.message) || "Bulk delete failed" });
                section.selectedIds = [];
                self.loadList();
            }).finally(function () {
                section.deletingSelected = false;
            });
        },
        openBulkUploadModal() {
            var section = this.s;
            section.showBulkUploadModal = true;
            section.bulkStep = "select";
            section.bulkDragOver = false;
            section.bulkFileName = "";
            section.bulkValidRows = [];
            section.bulkValidationErrors = [];
            section.bulkUploading = false;
            section.bulkProgressCurrent = 0;
            section.bulkProgressTotal = 0;
            section.bulkCurrentName = "";
            section.bulkSuccessCount = 0;
            section.bulkFailCount = 0;
            section.bulkUploadErrors = [];
        },
        closeBulkUploadModal() {
            this.resetBulkState(true);
            this.s.showBulkUploadModal = false;
        },
        resetBulkState(clearModal) {
            var section = this.s;
            section.bulkStep = "select";
            section.bulkDragOver = false;
            section.bulkFileName = "";
            section.bulkValidRows = [];
            section.bulkValidationErrors = [];
            section.bulkUploading = false;
            section.bulkProgressCurrent = 0;
            section.bulkProgressTotal = 0;
            section.bulkCurrentName = "";
            section.bulkSuccessCount = 0;
            section.bulkFailCount = 0;
            section.bulkUploadErrors = [];
            if (this.$refs && this.$refs.bulkFileInput) this.$refs.bulkFileInput.value = null;
            if (clearModal) section.showBulkUploadModal = false;
        },
        resetBulkSelection() {
            this.resetBulkState(false);
        },
        triggerBulkFilePicker() {
            if (this.$refs && this.$refs.bulkFileInput) this.$refs.bulkFileInput.click();
        },
        downloadBulkTemplate() {
            try {
                var tpl = BULK_TEMPLATES[this.activeTab];
                var link = document.createElement("a");
                link.href = tpl.url;
                link.download = tpl.filename;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (e) {
                this.toastAlert({ icon: "error", title: "Failed to download template" });
            }
        },
        onBulkFileDrop(event) {
            this.s.bulkDragOver = false;
            if (!event || !event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files.length) return;
            this.parseBulkXlsxFile(event.dataTransfer.files[0]);
        },
        onBulkFileSelected(event) {
            var input = event && event.target ? event.target : null;
            if (!input || !input.files || !input.files.length) return;
            this.parseBulkXlsxFile(input.files[0]);
        },
        parseBulkXlsxFile(file) {
            var self = this;
            this.s.bulkUploading = false;
            this.s.bulkStep = "review";
            this.s.bulkFileName = file && file.name ? file.name : "Uploaded file";
            this.s.bulkValidRows = [];
            this.s.bulkValidationErrors = [];
            this.s.bulkUploadErrors = [];
            var reader = new FileReader();
            reader.onload = function (e) {
                try {
                    var data = e && e.target ? e.target.result : null;
                    if (!data) {
                        self.s.bulkValidationErrors = [{ row: 0, message: "Could not read Excel file." }];
                        return;
                    }
                    var workbook = xlsx.read(data, { type: "array" });
                    var sheetName = workbook.SheetNames && workbook.SheetNames.length ? workbook.SheetNames[0] : null;
                    if (!sheetName) {
                        self.s.bulkValidationErrors = [{ row: 0, message: "Excel sheet not found." }];
                        return;
                    }
                    var aoa = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
                    var parsed = self.validateAndBuildBulkRows(aoa);
                    self.s.bulkValidRows = parsed.validRows;
                    self.s.bulkValidationErrors = parsed.errors;
                } catch (err) {
                    self.s.bulkValidationErrors = [{ row: 0, message: (err && err.message) ? err.message : "Failed to parse Excel file." }];
                }
            };
            reader.onerror = function () {
                self.s.bulkValidationErrors = [{ row: 0, message: "Failed to read Excel file." }];
            };
            reader.readAsArrayBuffer(file);
        },
        validateAndBuildBulkRows(aoa) {
            if (this.activeTab === "areas") return this.validateAreasBulkRows(aoa);
            if (this.activeTab === "transport") return this.validateTransportBulkRows(aoa);
            return this.validateHelipadBulkRows(aoa);
        },
        validateAreasBulkRows(aoa) {
            var errors = [];
            var validRows = [];
            if (!aoa || !aoa.length) return { validRows: [], errors: [{ row: 0, message: "The Excel file is empty." }] };
            var headerRow = aoa[0] || [];
            var headerIndex = {};
            for (var i = 0; i < headerRow.length; i++) {
                var hn = normalizeHeader(headerRow[i]);
                if (hn) headerIndex[hn] = i;
            }
            var idxLoc = resolveColumnIndex(headerIndex, "PARKING LOCATION", ["LOCATION", "PARKING LOC"]);
            if (idxLoc === undefined) {
                return { validRows: [], errors: [{ row: 1, message: "Missing required column: Parking Location." }] };
            }
            var idxSlots = resolveColumnIndex(headerIndex, "SLOTS", ["PARKING SLOTS", "NO OF PARKING SLOTS"]);
            var idxVip = resolveColumnIndex(headerIndex, "VIP", ["VIP PARKING"]);
            var idxDet = resolveColumnIndex(headerIndex, "DETERMINATION", []);
            var idxCharge = resolveColumnIndex(headerIndex, "CAR CHARGE", ["HAS CAR CHARGE"]);
            var idxIO = resolveColumnIndex(headerIndex, "INSIDE/OUTSIDE", ["INSIDE OUTSIDE", "INSIDE / OUTSIDE"]);
            var idxPaid = resolveColumnIndex(headerIndex, "PAID", ["PAID PARKING", "IS PAID PARKING"]);
            var idxShaded = resolveColumnIndex(headerIndex, "SHADED", []);
            var idxUg = resolveColumnIndex(headerIndex, "UNDERGROUND", ["IS UNDERGROUND"]);
            var idxSensor = resolveColumnIndex(headerIndex, "SENSOR", ["HAS SENSOR"]);
            var idxRemarks = resolveColumnIndex(headerIndex, "REMARKS", ["REMARK", "NOTES"]);
            for (var rowIndex = 1; rowIndex < aoa.length; rowIndex++) {
                var excelRow = rowIndex + 1;
                var row = aoa[rowIndex] || [];
                var loc = cellAt(row, idxLoc);
                if (loc === "" && cellAt(row, idxSlots) === "" && cellAt(row, idxVip) === "" && cellAt(row, idxDet) === "" && cellAt(row, idxCharge) === "" && cellAt(row, idxIO) === "" && cellAt(row, idxPaid) === "" && cellAt(row, idxShaded) === "" && cellAt(row, idxUg) === "" && cellAt(row, idxSensor) === "" && cellAt(row, idxRemarks) === "") continue;
                if (!loc) { errors.push({ row: excelRow, message: "Parking location is required." }); continue; }
                var slots = parseIntegerValue(cellAt(row, idxSlots), "Slots");
                var vip = parseIntegerValue(cellAt(row, idxVip), "VIP");
                var det = parseIntegerValue(cellAt(row, idxDet), "Determination");
                var charge = parseBooleanValue(cellAt(row, idxCharge));
                var paid = parseBooleanValue(cellAt(row, idxPaid));
                var ug = parseBooleanValue(cellAt(row, idxUg));
                var sensor = parseBooleanValue(cellAt(row, idxSensor));
                if (slots.error) { errors.push({ row: excelRow, message: slots.error }); continue; }
                if (vip.error) { errors.push({ row: excelRow, message: vip.error }); continue; }
                if (det.error) { errors.push({ row: excelRow, message: det.error }); continue; }
                if (charge.error) { errors.push({ row: excelRow, message: charge.error }); continue; }
                if (paid.error) { errors.push({ row: excelRow, message: paid.error }); continue; }
                if (ug.error) { errors.push({ row: excelRow, message: ug.error }); continue; }
                if (sensor.error) { errors.push({ row: excelRow, message: sensor.error }); continue; }
                var ioVal = cellAt(row, idxIO);
                validRows.push({
                    excelRow: excelRow,
                    parking_location: loc,
                    parking_slots: slots.value,
                    vip_parking: vip.value,
                    determination: det.value,
                    has_car_charge: charge.value,
                    inside_outside: ioVal !== "" ? ioVal : null,
                    is_paid_parking: paid.value,
                    shaded: cellAt(row, idxShaded) !== "" ? cellAt(row, idxShaded) : null,
                    is_underground: ug.value,
                    has_sensor: sensor.value,
                    remarks: cellAt(row, idxRemarks) !== "" ? cellAt(row, idxRemarks) : null,
                });
            }
            if (!validRows.length && !errors.length) errors.push({ row: 0, message: "No valid rows found." });
            return { validRows: validRows, errors: errors };
        },
        validateTransportBulkRows(aoa) {
            var errors = [];
            var validRows = [];
            if (!aoa || !aoa.length) return { validRows: [], errors: [{ row: 0, message: "The Excel file is empty." }] };
            var headerRow = aoa[0] || [];
            var headerIndex = {};
            for (var i = 0; i < headerRow.length; i++) {
                var hn = normalizeHeader(headerRow[i]);
                if (hn) headerIndex[hn] = i;
            }
            var idxService = resolveColumnIndex(headerIndex, "SERVICE", ["SERVICE NAME"]);
            if (idxService === undefined) return { validRows: [], errors: [{ row: 1, message: "Missing required column: Service." }] };
            var idxAvail = resolveColumnIndex(headerIndex, "AVAILABILITY", []);
            var idxPass = resolveColumnIndex(headerIndex, "NO. PASSENGERS", ["NO OF PASSENGERS", "PASSENGER CAPACITY", "PASSENGERS"]);
            var idxRemarks = resolveColumnIndex(headerIndex, "REMARKS", ["REMARK", "NOTES"]);
            for (var rowIndex = 1; rowIndex < aoa.length; rowIndex++) {
                var excelRow = rowIndex + 1;
                var row = aoa[rowIndex] || [];
                var service = cellAt(row, idxService);
                if (service === "" && cellAt(row, idxAvail) === "" && cellAt(row, idxPass) === "" && cellAt(row, idxRemarks) === "") continue;
                if (!service) { errors.push({ row: excelRow, message: "Service name is required." }); continue; }
                validRows.push({
                    excelRow: excelRow,
                    service_name: service,
                    availability: cellAt(row, idxAvail) !== "" ? cellAt(row, idxAvail) : null,
                    passenger_capacity: cellAt(row, idxPass) !== "" ? cellAt(row, idxPass) : null,
                    remarks: cellAt(row, idxRemarks) !== "" ? cellAt(row, idxRemarks) : null,
                });
            }
            if (!validRows.length && !errors.length) errors.push({ row: 0, message: "No valid rows found." });
            return { validRows: validRows, errors: errors };
        },
        validateHelipadBulkRows(aoa) {
            var errors = [];
            var validRows = [];
            if (!aoa || !aoa.length) return { validRows: [], errors: [{ row: 0, message: "The Excel file is empty." }] };
            var headerRow = aoa[0] || [];
            var headerIndex = {};
            for (var i = 0; i < headerRow.length; i++) {
                var hn = normalizeHeader(headerRow[i]);
                if (hn) headerIndex[hn] = i;
            }
            var idxService = resolveColumnIndex(headerIndex, "SERVICE", ["SERVICE NAME"]);
            if (idxService === undefined) return { validRows: [], errors: [{ row: 1, message: "Missing required column: Service." }] };
            var idxQty = resolveColumnIndex(headerIndex, "QUANTITY", []);
            var idxLoc = resolveColumnIndex(headerIndex, "LOCATION", []);
            var idxRemarks = resolveColumnIndex(headerIndex, "REMARKS", ["REMARK", "NOTES"]);
            for (var rowIndex = 1; rowIndex < aoa.length; rowIndex++) {
                var excelRow = rowIndex + 1;
                var row = aoa[rowIndex] || [];
                var service = cellAt(row, idxService);
                if (service === "" && cellAt(row, idxQty) === "" && cellAt(row, idxLoc) === "" && cellAt(row, idxRemarks) === "") continue;
                if (!service) { errors.push({ row: excelRow, message: "Service name is required." }); continue; }
                var qty = parseIntegerValue(cellAt(row, idxQty), "Quantity");
                if (qty.error) { errors.push({ row: excelRow, message: qty.error }); continue; }
                validRows.push({
                    excelRow: excelRow,
                    service_name: service,
                    quantity: qty.value,
                    location: cellAt(row, idxLoc) !== "" ? cellAt(row, idxLoc) : null,
                    remarks: cellAt(row, idxRemarks) !== "" ? cellAt(row, idxRemarks) : null,
                });
            }
            if (!validRows.length && !errors.length) errors.push({ row: 0, message: "No valid rows found." });
            return { validRows: validRows, errors: errors };
        },
        async startBulkUpload() {
            var section = this.s;
            var cfg = this.cfg;
            if (!section.bulkValidRows.length) {
                this.toastAlert({ icon: "warning", title: "No rows to upload." });
                return;
            }
            section.bulkUploading = true;
            section.bulkStep = "uploading";
            section.bulkProgressTotal = section.bulkValidRows.length;
            section.bulkProgressCurrent = 0;
            section.bulkCurrentName = "";
            section.bulkSuccessCount = 0;
            section.bulkFailCount = 0;
            section.bulkUploadErrors = [];
            try {
                var chunkSize = 200;
                var offset = 0;
                while (offset < section.bulkValidRows.length) {
                    var chunk = section.bulkValidRows.slice(offset, offset + chunkSize);
                    var first = chunk && chunk.length ? chunk[0] : null;
                    section.bulkCurrentName = first
                        ? (this.activeTab === "areas" ? (first.parking_location || "—") : (first.service_name || "—"))
                        : "";
                    var rows = chunk.map(function (r) {
                        var row = { row: r.excelRow };
                        if (cfg.listRoot === "facilityParkingAreas") {
                            row.parking_location = r.parking_location;
                            row.parking_slots = r.parking_slots;
                            row.vip_parking = r.vip_parking;
                            row.determination = r.determination;
                            row.has_car_charge = r.has_car_charge;
                            row.inside_outside = r.inside_outside;
                            row.is_paid_parking = r.is_paid_parking;
                            row.shaded = r.shaded;
                            row.is_underground = r.is_underground;
                            row.has_sensor = r.has_sensor;
                            row.remarks = r.remarks;
                        } else if (cfg.listRoot === "facilityParkingTransportServices") {
                            row.service_name = r.service_name;
                            row.availability = r.availability;
                            row.passenger_capacity = r.passenger_capacity;
                            row.remarks = r.remarks;
                        } else {
                            row.service_name = r.service_name;
                            row.quantity = r.quantity;
                            row.location = r.location;
                            row.remarks = r.remarks;
                        }
                        return row;
                    });
                    try {
                        var res = await this.$apollo.mutate({
                            mutation: cfg.createBulkMutation,
                            variables: { input: { entity: this.entity, campus: this.campus, rows: rows } },
                        });
                        var root = res && res.data ? res.data[cfg.createBulkRoot] : null;
                        var payloadData = root && root.data ? root.data : null;
                        if (payloadData) {
                            section.bulkSuccessCount += payloadData.success_count || 0;
                            section.bulkFailCount += payloadData.fail_count || 0;
                            if (payloadData.results && payloadData.results.length) {
                                for (var j = 0; j < payloadData.results.length; j++) {
                                    var rr = payloadData.results[j];
                                    if (rr && rr.error) {
                                        section.bulkUploadErrors.push({ row: rr.row, message: rr.message || "Failed to create record" });
                                    }
                                }
                            }
                        } else {
                            var msg = (root && root.message) ? root.message : "Bulk upload failed";
                            for (var k = 0; k < chunk.length; k++) {
                                section.bulkFailCount += 1;
                                section.bulkUploadErrors.push({ row: chunk[k].excelRow, message: msg });
                            }
                        }
                    } catch (err) {
                        var catchMessage = (err && err.message) ? err.message : "Bulk upload failed";
                        for (var m = 0; m < chunk.length; m++) {
                            section.bulkFailCount += 1;
                            section.bulkUploadErrors.push({ row: chunk[m].excelRow, message: catchMessage });
                        }
                    }
                    offset += chunk.length;
                    section.bulkProgressCurrent = offset;
                }
            } finally {
                section.bulkUploading = false;
                section.bulkStep = "done";
                this.loadList();
                this.$emit("changed");
            }
        },
        exportFileName() {
            return "ADPOLY_" + this.campus + "_" + this.cfg.exportSuffix;
        },
        mapExportRow(item) {
            if (this.activeTab === "areas") {
                return {
                    "Parking Location": item.parking_location || "",
                    Slots: item.parking_slots !== null && item.parking_slots !== undefined ? item.parking_slots : "",
                    VIP: item.vip_parking !== null && item.vip_parking !== undefined ? item.vip_parking : "",
                    Determination: item.determination !== null && item.determination !== undefined ? item.determination : "",
                    "Car Charge": this.formatBooleanDisplay(item.has_car_charge),
                    "Inside/Outside": item.inside_outside || "",
                    Paid: this.formatBooleanDisplay(item.is_paid_parking),
                    Shaded: item.shaded || "",
                    Underground: this.formatBooleanDisplay(item.is_underground),
                    Sensor: this.formatBooleanDisplay(item.has_sensor),
                    Remarks: item.remarks || "",
                };
            }
            if (this.activeTab === "transport") {
                return {
                    Service: item.service_name || "",
                    Availability: item.availability || "",
                    "No. Passengers": item.passenger_capacity || "",
                    Remarks: item.remarks || "",
                };
            }
            return {
                Service: item.service_name || "",
                Quantity: item.quantity !== null && item.quantity !== undefined ? item.quantity : "",
                Location: item.location || "",
                Remarks: item.remarks || "",
            };
        },
        generateReport: function () {
            var store = this.$store;
            var apolloClient = this.$apollo && this.$apollo.provider
                ? this.$apollo.provider.defaultClient
                : null;
            if (!apolloClient) return;

            var fileName = this.exportFileName() + ".xlsx";
            var sheetName = this.activeTab === "areas" ? "Parking Areas" : (this.activeTab === "transport" ? "Transport Services" : "Helipads");

            // Snapshot query config and variables
            var listQuery = this.cfg.listQuery;
            var listRoot = this.cfg.listRoot;
            var entity = this.entity;
            var campus = this.campus;
            var search = this.s.search || null;
            var activeTab = this.activeTab;
            var insideOutside = (activeTab === "areas" && this.s.filters)
                ? (this.s.filters.inside_outside || null)
                : null;
            var mapRow = this.mapExportRow.bind(this);

            function buildVars(page, limit) {
                var vars = {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    search: search,
                };
                if (activeTab === "areas") {
                    vars.inside_outside = insideOutside;
                }
                return vars;
            }

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: 1,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);
                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: 5,
                        currentSheet: sheetName,
                    });

                    var allRows = [];
                    var exportPage = 1;

                    function fetchNext() {
                        apolloClient
                            .query({
                                query: listQuery,
                                variables: buildVars(exportPage, 500),
                                fetchPolicy: "network-only",
                            })
                            .then(function (response) {
                                var payload = response && response.data && response.data[listRoot]
                                    ? response.data[listRoot]
                                    : null;
                                if (!payload || payload.error) {
                                    throw new Error((payload && payload.message) || "Failed to fetch data for export.");
                                }
                                var rows = payload.data || [];
                                if (rows.length) {
                                    for (var i = 0; i < rows.length; i++) {
                                        allRows.push(mapRow(rows[i]));
                                    }
                                }
                                var paginator = payload.paginatorInfo || {};
                                var hasMore = !!paginator.hasMorePages;
                                var total = paginator.total || allRows.length;
                                var percent = total > 0 ? Math.min(95, Math.round((allRows.length / total) * 100)) : 50;
                                store.dispatch("exportManager/updateJob", {
                                    id: jobId,
                                    progress: percent,
                                    currentSheet: sheetName + " (" + allRows.length + " rows)",
                                });
                                exportPage += 1;
                                if (hasMore && exportPage <= 100) {
                                    fetchNext();
                                } else {
                                    finishExport();
                                }
                            })
                            .catch(function (error) {
                                store.dispatch("exportManager/failJob", {
                                    id: jobId,
                                    error: (error && error.message) || "Export failed",
                                });
                            });
                    }

                    function finishExport() {
                        try {
                            if (allRows.length === 0) {
                                store.dispatch("exportManager/failJob", { id: jobId, error: "No records to export" });
                                return;
                            }
                            var worksheet = xlsx.utils.json_to_sheet(allRows);
                            var workbook = xlsx.utils.book_new();
                            xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);
                            xlsx.writeFile(workbook, fileName);
                            store.dispatch("exportManager/completeJob", { id: jobId, rowCount: allRows.length });
                        } catch (error) {
                            store.dispatch("exportManager/failJob", { id: jobId, error: (error && error.message) || "Export failed" });
                        }
                    }

                    fetchNext();
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}

.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}

@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem 1rem;
}

.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.ds-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    flex: 1 1 auto;
}

.ds-filter__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin-bottom: 0.25rem;
}

.ds-select,
.ds-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: #111827;
}

.ds-filter-input {
    min-width: 10rem;
    height: 2.25rem;
}

.ds-textarea {
    resize: vertical;
    min-height: 5rem;
}

.ds-select {
    min-width: 8.5rem;
    height: 2.25rem;
}

.ds-select:focus,
.ds-input:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
}

.ds-search {
    flex: 1 1 14rem;
    min-width: 12rem;
    max-width: 22rem;
}

.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.55rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.ds-btn--primary {
    background: #0f766e;
    color: #ffffff;
    border: 1px solid #0f766e;
}

.ds-btn--primary:hover {
    background: #115e59;
}

.ds-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.ds-export-btn:hover {
    border-color: rgba(16, 185, 129, 0.45);
    background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.ds-export-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.ds-export-btn__icon {
    font-size: 1.05rem;
    color: #059669;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.ds-btn--ghost {
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
}

.ds-btn--ghost:hover {
    background: #f9fafb;
}

.ds-btn--danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #dc2626;
}

.ds-btn--danger:hover {
    background: #b91c1c;
}

.ds-btn--danger:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.ds-checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: #0f766e;
}

.ds-bulk {
    padding: 1rem 2rem;
}

.ds-bulk__banner {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: #ecfeff;
    border: 1px solid rgba(15, 118, 110, 0.18);
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
}

.ds-bulk__banner-icon {
    font-size: 1.25rem;
    color: #0f766e;
    line-height: 1;
}

.ds-bulk__banner-title {
    font-size: 1rem;
    font-weight: 800;
    color: #0f766e;
    margin: 0;
}

.ds-bulk__banner-hint {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.35;
}

.ds-bulk__section {
    padding: 0 0.25rem 0.5rem;
}

.ds-bulk__dropzone {
    border: 2px dashed rgba(15, 118, 110, 0.35);
    background: #f8fafc;
    border-radius: 0.85rem;
    padding: 1.5rem 1rem;
    text-align: center;
    cursor: pointer;
}

.ds-bulk__dropzone--active {
    border-color: rgba(15, 118, 110, 0.8);
    background: #ecfeff;
}

.ds-bulk__dropzone-icon {
    font-size: 1.5rem;
    color: #0f766e;
}

.ds-bulk__dropzone-title {
    margin: 0.75rem 0 0.25rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f766e;
}

.ds-bulk__dropzone-sub {
    margin: 0;
    font-size: 0.82rem;
    color: #6b7280;
}

.ds-bulk__tips {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.ds-bulk__link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(59, 130, 246, 0.25);
    background: rgba(59, 130, 246, 0.06);
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: #1d4ed8;
    cursor: pointer;
}

.ds-bulk__link-btn:hover {
    background: rgba(59, 130, 246, 0.12);
    border-color: rgba(59, 130, 246, 0.45);
}

.ds-bulk__tips-text {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
}

.ds-bulk__file-card {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
}

.ds-bulk__file-icon {
    color: #0f766e;
    font-size: 1.1rem;
}

.ds-bulk__file-name {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 800;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ds-bulk__file-meta {
    margin: 0.25rem 0 0;
    font-size: 0.82rem;
    color: #6b7280;
}

.ds-bulk__errors {
    margin-top: 0.75rem;
    border: 1px solid rgba(239, 68, 68, 0.2);
    background: rgba(254, 242, 242, 0.7);
    border-radius: 0.9rem;
    padding: 1rem;
}

.ds-bulk__errors-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 800;
    color: #b91c1c;
    margin-bottom: 0.75rem;
}

.ds-bulk__errors-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.ds-bulk__error-item {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: baseline;
    color: #7f1d1d;
    font-size: 0.85rem;
}

.ds-bulk__error-row {
    font-weight: 800;
}

.ds-bulk__ready {
    margin-top: 1rem;
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.22);
    border-radius: 0.9rem;
    padding: 0.9rem 1rem;
}

.ds-bulk__ready-icon {
    margin-top: 0.1rem;
    color: #047857;
    font-size: 1.25rem;
}

.ds-bulk__ready-title {
    margin: 0;
    font-weight: 900;
    color: #047857;
    line-height: 1.25;
}

.ds-bulk__ready-sub {
    margin: 0.35rem 0 0;
    color: #065f46;
    font-size: 0.85rem;
}

.ds-bulk__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
}

.ds-bulk__footer--single {
    justify-content: flex-end;
}

.ds-bulk__progress-card {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 1rem;
}

.ds-bulk__progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.ds-bulk__progress-title {
    font-weight: 800;
    color: #111827;
}

.ds-bulk__progress-percent {
    font-weight: 900;
    color: #0f766e;
}

.ds-bulk__progress-track {
    height: 10px;
    background: #f3f4f6;
    border-radius: 9999px;
    overflow: hidden;
}

.ds-bulk__progress-fill {
    height: 10px;
    background: #0f766e;
    border-radius: 9999px;
}

.ds-bulk__progress-sub {
    margin: 0.75rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
}

.ds-bulk__progress-name {
    margin: 0.5rem 0 0;
    color: #374151;
    font-size: 0.85rem;
    font-weight: 700;
}

.ds-bulk__summary {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.ds-bulk__summary-stat {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
}

.ds-bulk__summary-stat--ok {
    border-color: rgba(16, 185, 129, 0.3);
    background: rgba(16, 185, 129, 0.08);
}

.ds-bulk__summary-stat--fail {
    border-color: rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.08);
}

.ds-bulk__summary-stat--ok .ds-bulk__summary-value {
    color: #047857;
}

.ds-bulk__summary-stat--fail .ds-bulk__summary-value {
    color: #b91c1c;
}

.ds-bulk__summary-value {
    font-size: 1.6rem;
    font-weight: 900;
    margin: 0;
    color: #111827;
}

.ds-bulk__summary-label {
    margin: 0.25rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
    font-weight: 700;
}

.ds-table-shell {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: #0f766e;
    white-space: nowrap;
}

.ds-th:first-child {
    border-top-left-radius: 0.7rem;
}

.ds-th:last-child {
    border-top-right-radius: 0.7rem;
}

.ds-row:nth-child(even) {
    background: #f0fdfa;
}

.ds-row:hover {
    background: #ccfbf1;
}

.ds-td {
    padding: 0.7rem 1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
    vertical-align: middle;
}

.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: #f3f4f6;
    margin-left: 0.35rem;
}

.ds-icon-btn:hover {
    background: #e0e7ff;
}

.ds-icon-btn--danger:hover {
    background: #fee2e2;
}

.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}

.ds-form__header {
    padding: 1.25rem 1.5rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
}

.ds-form__body {
    padding: 1.25rem 1.5rem;
}

.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
}

.ds-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.cp-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem 1rem 0;
}

@media (min-width: 640px) {
    .cp-tabs {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.cp-tab-btn {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cp-tab-btn:hover {
    background: #f9fafb;
}

.cp-tab-btn--active {
    background: #0f766e;
    border-color: #0f766e;
    color: #ffffff;
}
</style>
